import {
  Image,
  Box,
  Flex,
  Heading,
  Avatar,
  Text,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  useDisclosure,
  Input,
  Spinner,
  Circle,
} from '@chakra-ui/react';
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { IconCamera } from 'assets/icons';
import { bgProfile } from 'assets/images';
import Button from 'components/Button';
import Drawer from 'components/Drawer';
import FallbackAvatar from 'components/FallbackAvatar';
import { ConfirmationModal } from 'components/Modal';
import { initialProfileFormData } from 'containers/Profile/ProfileForm/schema';
import ChangePassword from 'containers/Profile/RightDrawer/ChangePassword';
import { useChangeProfileImage } from 'hooks/profile/useChangeProfileImage';
import { useFetchUserDetails } from 'hooks/profile/useFetchUserDetail';
import { useRemoveProfileImage } from 'hooks/profile/useRemoveProfileImage';
import { useUserInfo } from 'store/useUserStore';

import ProfileForm from './ProfileForm';

const Profile: React.FC<unknown> = () => {
  const { t } = useTranslation(['profile', 'common']);
  const [profileFormData, setProfileFormData] = useState<typeof initialProfileFormData>(initialProfileFormData);
  const imageRef = useRef<any>();
  const userInfo = useUserInfo();

  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isDeleteModalOpen, onOpen: onDeleteModalOpen, onClose: onDeleteModalClose } = useDisclosure();

  const { data } = useFetchUserDetails({ enabled: false });
  const { status: changeProfileStatus, mutate: mutateChangeProfileImage } = useChangeProfileImage();
  const { status: removeProfileImageStatus, mutateAsync: mutateAsyncRemoveProfileImage } = useRemoveProfileImage();

  useEffect(() => {
    const formData: typeof initialProfileFormData = {
      firstName: userInfo?.firstName || '',
      lastName: userInfo?.lastName || '',
      email: userInfo?.email || '',
      phone: userInfo?.phone || '',
    };

    setProfileFormData(formData);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePhotoChange = () => {
    imageRef.current.click();
  };

  const handleViewPhoto = () => {
    onOpen();
  };

  const handleRemovePhoto = () => {
    onDeleteModalOpen();
  };

  const removePhoto = () => {
    mutateAsyncRemoveProfileImage().then(() => {
      onDeleteModalClose();
    });
  };

  const handleFileChange = (event: any) => {
    mutateChangeProfileImage({ employee_image: event.target.files[0] });
  };

  return (
    <>
      <Modal onClose={onClose} isOpen={isOpen} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton />
          <Image
            width="100%"
            src={userInfo?.employee_image}
            objectFit="cover"
            fallbackSrc="https://bit.ly/3iuys5a"
            alt={userInfo?.firstName + userInfo?.lastName}
          />
        </ModalContent>
      </Modal>
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={onDeleteModalClose}
        title={t('profile:deletePhotoModalTitle')}
        subTitle={t('profile:deletePhotoModalSubTitle')}
        confirmButtonLabel={t('common:delete')}
        confirmButtonProps={{
          bg: 'rgba(255, 83, 83, 0.1)',
          color: 'secondary.500',
          _hover: {
            bg: 'rgba(255, 83, 83, 0.2)',
          },
          isLoading: removeProfileImageStatus === 'loading',
          onClick: removePhoto,
        }}
      />
      <Box
        position="relative"
        pb="4"
        mx={{ base: 0, sm: 4, md: 6 }}
        my={{ base: 4, md: 6 }}
        bg="white"
        minHeight="100%"
        paddingBottom="40px"
        borderRadius="lg"
      >
        <Image
          src={bgProfile}
          objectFit="cover"
          width="100%"
          height="280px"
          fallback={<FallbackAvatar boxSize="280px" />}
        />
        <Flex alignItems="center" flexDirection="column">
          <div>
            <Flex
              mt="-125px"
              flexDirection={{ base: 'column', md: 'row' }}
              alignItems={{ base: 'center', md: 'flex-start' }}
              textAlign={{ base: 'center', md: 'left' }}
            >
              {/* Image */}
              <Box position="relative">
                <Image
                  width="200px"
                  height="200px"
                  objectFit="cover"
                  src={userInfo?.employee_image}
                  fallback={<FallbackAvatar boxSize="200px" />}
                  alt={userInfo?.firstName + userInfo?.lastName}
                />
                {changeProfileStatus === 'loading' && (
                  <Spinner position="absolute" top="calc(50% - 10px)" left="calc(50% - 10px)" />
                )}
                <Input
                  id="file"
                  display="none"
                  type="file"
                  name="employee_image"
                  accept="image/jpeg, image/png, image/svg+xml, image/jpg"
                  ref={imageRef}
                  onChange={handleFileChange}
                />
                <Menu isLazy closeOnBlur>
                  <MenuButton
                    position="absolute"
                    bottom="0"
                    right="0"
                    zIndex="100"
                    as={IconButton}
                    aria-label="photo Dropdown"
                    colorScheme="gray"
                    size="lg"
                    transform="translate(50%, 50%)"
                    icon={<Icon as={IconCamera} width="24px" height="21px" sx={{ '& path': { fill: 'gray.500' } }} />}
                    isRound
                  />
                  <MenuList>
                    <MenuItem onClick={handlePhotoChange}>{t('profile:changePhoto')}</MenuItem>
                    <MenuItem onClick={handleViewPhoto} isDisabled={!data?.payload?.userInfo?.employee_image}>
                      {t('profile:viewPhoto')}
                    </MenuItem>
                    <MenuItem
                      onClick={handleRemovePhoto}
                      color="secondary.700"
                      isDisabled={!data?.payload?.userInfo?.employee_image}
                    >
                      {t('profile:removePhoto')}
                    </MenuItem>
                  </MenuList>
                </Menu>
              </Box>

              {/* Image Desc */}
              <Box ml={{ md: '10' }} mt="4">
                <Heading as="h3" size="lg" color={{ md: 'white' }} mb="2">
                  <Flex>
                    <Text textTransform="capitalize" mr={2}>
                      {userInfo?.firstName}{' '}
                    </Text>{' '}
                    <Text textTransform="capitalize">{data?.payload.userInfo?.lastName}</Text>
                  </Flex>
                </Heading>
                <Flex alignItems="center" mb={{ base: 4, md: '16' }} gridGap={2}>
                  <Avatar size="xs" mr="2" />
                  <Text fontWeight="600" color={{ md: 'white' }}>
                    {userInfo?.roleName}
                  </Text>
                  <Circle size="7px" bg={{ base: 'primary.900', md: 'white' }} />
                  <Text textTransform="capitalize" size="sm" fontWeight="600" color={{ md: 'white' }}>
                    {data?.payload.companyInfo?.name}
                  </Text>
                </Flex>

                {/* Change Password Drawer */}
                <Drawer
                  drawerTrigger={onOpen => (
                    <Button
                      bg="secondary.50"
                      color="secondary.500"
                      size="sm"
                      fontWeight="semibold"
                      _hover={{
                        bg: 'secondary.100',
                      }}
                      onClick={onOpen}
                    >
                      {t('profile:changePassword')}
                    </Button>
                  )}
                >
                  {onClose => <ChangePassword onClose={onClose} />}
                </Drawer>
              </Box>
            </Flex>

            {/* Profile Form */}
            <div>
              <ProfileForm formData={profileFormData} />
            </div>
          </div>
        </Flex>
      </Box>
    </>
  );
};

export default Profile;
